import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  ShoppingCart,
  Check,
  X,
  Clock,
  CheckCircle,
  Printer,
  Truck,
  Package,
  MapPin,
  Mail,
  Loader2,
  FolderOpen,
  FolderCheck,
  Navigation,
  Share2
} from 'lucide-react';
import { toast } from 'sonner';
import { DriverRoutePicker, DriverRouteBanner, fetchDriverCounts } from '../../components/chauffeur/DriverRouteControls';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrderList = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const [autoOpenPakbon, setAutoOpenPakbon] = useState(true);
  const [pakbonTab, setPakbonTab] = useState('all'); // 'all' | 'to_print' | 'picked_up' | 'done'
  const navigate = useNavigate();
  
  // Transport dialog state
  const [transportDialogOpen, setTransportDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [transportData, setTransportData] = useState({
    transport_status: 'pending',
    transport_carrier: '',
    transport_tracking_number: '',
    transport_estimated_delivery: '',
    transport_notes: ''
  });
  const [savingTransport, setSavingTransport] = useState(false);

  // ============ HP ePrint (auto-print pakbon) ============
  const [eprintOpen, setEprintOpen] = useState(false);
  const [eprintEmail, setEprintEmail] = useState('');
  const [eprintEnabled, setEprintEnabled] = useState(false);
  const [eprintUpdatedAt, setEprintUpdatedAt] = useState(null);
  const [savingEprint, setSavingEprint] = useState(false);
  const [testingEprint, setTestingEprint] = useState(false);
  const [printingOrderId, setPrintingOrderId] = useState(null);

  // ============ Chauffeur Route (op route per chauffeur, max 13) ============
  const [driverCounts, setDriverCounts] = useState({ counts: {}, max: 13 });
  const refreshDriverCounts = () => fetchDriverCounts().then(setDriverCounts).catch(() => {});
  const onDriverChange = (orderId, driver, counts) => {
    setOrders((os) => os.map((o) => (o.id === orderId ? { ...o, assigned_driver: driver, on_route: !!driver } : o)));
    setDriverCounts((c) => ({ ...c, counts }));
  };
  useEffect(() => { refreshDriverCounts(); }, []);

  const loadEprintSettings = async () => {
    try {
      const res = await axios.get(`${API}/orders/eprint-settings`);
      setEprintEmail(res.data.eprint_email || '');
      setEprintEnabled(!!res.data.enabled);
      setEprintUpdatedAt(res.data.updated_at || null);
    } catch (e) {
      console.error('ePrint settings load failed', e);
    }
  };

  const saveEprintSettings = async () => {
    setSavingEprint(true);
    try {
      const res = await axios.put(`${API}/orders/eprint-settings`, {
        eprint_email: eprintEmail.trim(),
        enabled: eprintEnabled,
      });
      setEprintEmail(res.data.eprint_email || '');
      setEprintEnabled(!!res.data.enabled);
      setEprintUpdatedAt(res.data.updated_at || null);
      toast.success(res.data.enabled ? '📧 ePrint AAN — nieuwe orders worden automatisch geprint' : '📧 ePrint uitgeschakeld');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Opslaan mislukt');
    } finally {
      setSavingEprint(false);
    }
  };

  const testEprint = async () => {
    setTestingEprint(true);
    try {
      const res = await axios.post(`${API}/orders/eprint-settings/test`);
      toast.success(`✅ Test-pakbon verstuurd naar ${res.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test-verzending mislukt');
    } finally {
      setTestingEprint(false);
    }
  };

  const printOrderToEprint = async (orderId) => {
    setPrintingOrderId(orderId);
    try {
      const res = await axios.post(`${API}/orders/${orderId}/print-eprint`);
      toast.success(`📧 Pakbon verzonden naar printer (${res.data.to})`);
      fetchOrders();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verzenden naar printer mislukt');
    } finally {
      setPrintingOrderId(null);
    }
  };

  useEffect(() => { loadEprintSettings(); }, []);

  useEffect(() => {
    fetchOrders();
    
    // Poll for new orders every 30 seconds
    const interval = setInterval(() => {
      checkForNewOrders();
      refreshDriverCounts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  const checkForNewOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      const newOrders = response.data;
      
      // Check if there are new orders
      if (lastOrderCount > 0 && newOrders.length > lastOrderCount && autoOpenPakbon) {
        // Find the newest order
        const newestOrder = newOrders[0]; // Orders are sorted by date desc
        
        // Show notification
        toast.success(`🆕 Nieuwe order: ${newestOrder.motorcycle_brand} ${newestOrder.motorcycle_model}`, {
          duration: 10000,
          action: {
            label: 'Print Pakbon',
            onClick: () => window.open(`/pakbon/${newestOrder.id}?print=true`, '_blank')
          }
        });
        
        // Auto-open pakbon in new tab
        window.open(`/pakbon/${newestOrder.id}?print=true`, '_blank');
      }
      
      setLastOrderCount(newOrders.length);
      setOrders(newOrders);
    } catch (error) {
      console.error('Error checking for new orders:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
      setLastOrderCount(response.data.length);
    } catch (error) {
      // Only show error if it's not a cancelled request
      if (!axios.isCancel(error)) {
        console.error('Failed to load orders:', error);
        toast.error(t('adminOrders.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status?status=${status}`);
      toast.success(t('adminOrders.statusUpdated', { status: getStatusLabel(status) }));
      fetchOrders();
    } catch (error) {
      toast.error(t('adminOrders.statusUpdateFailed'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-blue-100 text-blue-800'
    };
    return <Badge className={styles[status]}>{getStatusLabel(status)}</Badge>;
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t('order.pending'),
      approved: t('order.approved'),
      rejected: t('order.rejected'),
      completed: t('order.completed')
    };
    return labels[status];
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const openTransportDialog = (order) => {
    setSelectedOrder(order);
    setTransportData({
      transport_status: order.transport_status || 'pending',
      transport_carrier: order.transport_carrier || '',
      transport_tracking_number: order.transport_tracking_number || '',
      transport_estimated_delivery: order.transport_estimated_delivery || '',
      transport_notes: order.transport_notes || ''
    });
    setTransportDialogOpen(true);
  };

  const saveTransportStatus = async () => {
    if (!selectedOrder) return;
    
    setSavingTransport(true);
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}/transport`, transportData);
      toast.success('Transport status bijgewerkt');
      setTransportDialogOpen(false);
      fetchOrders();
    } catch (error) {
      toast.error('Kon transport status niet bijwerken');
    } finally {
      setSavingTransport(false);
    }
  };

  const getTransportStatusLabel = (status) => {
    const labels = {
      pending: 'Wachtend',
      picked_up: 'Opgehaald',
      in_transit: 'Onderweg',
      delivered: 'Afgeleverd'
    };
    return labels[status] || status;
  };

  const getTransportBadge = (status) => {
    const styles = {
      pending: 'bg-zinc-100 text-zinc-700',
      picked_up: 'bg-amber-100 text-amber-700',
      in_transit: 'bg-blue-100 text-blue-700',
      delivered: 'bg-green-100 text-green-700'
    };
    return <Badge className={styles[status] || 'bg-zinc-100'}>{getTransportStatusLabel(status)}</Badge>;
  };

  // ============ Pakbon folder logic ============
  const markPakbonPickedUp = async (orderId, pickedUp) => {
    try {
      await axios.put(`${API}/orders/${orderId}/pakbon-picked-up`, { picked_up: pickedUp });
      toast.success(pickedUp ? '📦 Gemarkeerd als opgehaald' : '↶ Terug naar "Nog uitprinten"');
      fetchOrders();
    } catch (e) {
      toast.error('Kon status niet bijwerken');
    }
  };

  const markPakbonCompleted = async (orderId, completed) => {
    try {
      await axios.put(`${API}/orders/${orderId}/pakbon-complete`, { completed });
      toast.success(completed ? '✅ Pakbon afgehandeld' : '↶ Terug naar Opgehaald');
      fetchOrders();
    } catch (e) {
      toast.error('Kon status niet bijwerken');
    }
  };

  const toPrintCount = orders.filter(o => !o.pakbon_completed && !o.pakbon_picked_up).length;
  const pickedUpCount = orders.filter(o => !o.pakbon_completed && !!o.pakbon_picked_up).length;
  const doneCount = orders.filter(o => !!o.pakbon_completed).length;

  const visibleOrders = orders.filter(order => {
    if (pakbonTab === 'all') return true;
    const isCompleted = !!order.pakbon_completed;
    const isPickedUp = !!order.pakbon_picked_up;
    if (pakbonTab === 'to_print') return !isCompleted && !isPickedUp;
    if (pakbonTab === 'picked_up') return !isCompleted && isPickedUp;
    if (pakbonTab === 'done') return isCompleted;
    return true;
  });

  if (loading) {
    return (
      <Layout requiredRole="admin">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout requiredRole="admin">
      <div className="content-header">
        <div>
          <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-zinc-900">
            {t('nav.orders')}
          </h1>
          <p className="text-zinc-500 mt-1">{orders.length} {t('orders.totalOrders')}</p>
        </div>
        
        {/* Auto-print toggle */}
        <div className="flex items-center gap-3">
          <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${autoOpenPakbon ? 'border-green-500 bg-green-50' : 'border-zinc-200'}`}>
            <input
              type="checkbox"
              checked={autoOpenPakbon}
              onChange={(e) => {
                setAutoOpenPakbon(e.target.checked);
                toast.success(e.target.checked ? '🖨️ Auto-print AAN' : '🖨️ Auto-print UIT');
              }}
              className="w-4 h-4 rounded"
            />
            <Printer className={`w-4 h-4 ${autoOpenPakbon ? 'text-green-600' : 'text-zinc-400'}`} />
            <span className={`text-sm font-medium ${autoOpenPakbon ? 'text-green-700' : 'text-zinc-500'}`}>
              Auto-print pakbon
            </span>
          </label>

          {/* HP ePrint toggle */}
          <button
            type="button"
            onClick={() => setEprintOpen(o => !o)}
            data-testid="eprint-settings-toggle"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${eprintEnabled && eprintEmail ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-zinc-200 text-zinc-500'}`}
            title="HP ePrint instellingen"
          >
            <Mail className={`w-4 h-4 ${eprintEnabled && eprintEmail ? 'text-blue-600' : 'text-zinc-400'}`} />
            <span className="text-sm font-medium">
              HP ePrint {eprintEnabled && eprintEmail ? 'AAN' : 'UIT'}
            </span>
          </button>
        </div>
      </div>

      {eprintOpen && (
        <div className="bg-white border border-blue-200 rounded-lg p-6 mb-6" data-testid="eprint-settings-panel">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-bold text-zinc-900 flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                HP ePrint — automatisch printen naar draadloze printer
              </h3>
              <p className="text-xs text-zinc-500 mt-1">
                Voer het unieke <b>@hpeprint.com</b> adres van je HP printer in. Bij elke nieuwe bestelling stuurt Moto Import de pakbon-PDF automatisch als bijlage — de printer print hem direct.
              </p>
              <p className="text-xs text-amber-700 mt-2">
                ⚠️ Vergeet niet <code>Motoimportbv@gmail.com</code> als toegestane afzender toe te voegen in HP Smart, anders weigert de printer de mail.
              </p>
            </div>
            <button onClick={() => setEprintOpen(false)} className="text-zinc-400 hover:text-zinc-700 text-xl leading-none">×</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs font-bold text-zinc-600 uppercase tracking-wider block mb-1">
                ePrint e-mailadres
              </label>
              <input
                type="email"
                value={eprintEmail}
                onChange={(e) => setEprintEmail(e.target.value)}
                placeholder="voorbeeld-random@hpeprint.com"
                className="w-full border-2 border-zinc-300 rounded-lg px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                data-testid="eprint-email-input"
              />
            </div>
            <label className="flex items-center gap-2 pb-2">
              <input
                type="checkbox"
                checked={eprintEnabled}
                onChange={(e) => setEprintEnabled(e.target.checked)}
                className="w-4 h-4 rounded"
                data-testid="eprint-enabled-checkbox"
              />
              <span className="text-sm font-medium text-zinc-700">Automatisch printen bij nieuwe orders</span>
            </label>
          </div>

          <div className="flex items-center gap-2 mt-4">
            <Button
              onClick={saveEprintSettings}
              disabled={savingEprint}
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              data-testid="eprint-save-btn"
            >
              {savingEprint ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Opslaan
            </Button>
            <Button
              onClick={testEprint}
              disabled={testingEprint || !eprintEmail}
              variant="outline"
              className="gap-2"
              data-testid="eprint-test-btn"
            >
              {testingEprint ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              Test-pakbon versturen
            </Button>
            {eprintUpdatedAt && (
              <span className="text-xs text-zinc-400 ml-auto">
                Laatst bijgewerkt: {new Date(eprintUpdatedAt).toLocaleString('nl-NL')}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="content-body" data-testid="order-list">
        <DriverRouteBanner counts={driverCounts.counts} max={driverCounts.max} />

        {/* Pakbon Folder Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap" data-testid="pakbon-folder-tabs">
          <Button
            variant={pakbonTab === 'all' ? 'default' : 'outline'}
            onClick={() => setPakbonTab('all')}
            className={pakbonTab === 'all' ? 'bg-zinc-800 hover:bg-zinc-900 gap-2' : 'gap-2'}
            data-testid="tab-all"
            size="sm"
          >
            <ShoppingCart className="w-4 h-4" />
            Alle ({orders.length})
          </Button>
          <Button
            variant={pakbonTab === 'to_print' ? 'default' : 'outline'}
            onClick={() => setPakbonTab('to_print')}
            className={pakbonTab === 'to_print' ? 'bg-amber-600 hover:bg-amber-700 gap-2' : 'gap-2'}
            data-testid="tab-to-print"
            size="sm"
          >
            <FolderOpen className="w-4 h-4" />
            Nog uitprinten ({toPrintCount})
          </Button>
          <Button
            variant={pakbonTab === 'picked_up' ? 'default' : 'outline'}
            onClick={() => setPakbonTab('picked_up')}
            className={pakbonTab === 'picked_up' ? 'bg-blue-600 hover:bg-blue-700 gap-2' : 'gap-2'}
            data-testid="tab-picked-up"
            size="sm"
          >
            <Package className="w-4 h-4" />
            Opgehaald ({pickedUpCount})
          </Button>
          <Button
            variant={pakbonTab === 'done' ? 'default' : 'outline'}
            onClick={() => setPakbonTab('done')}
            className={pakbonTab === 'done' ? 'bg-green-600 hover:bg-green-700 gap-2' : 'gap-2'}
            data-testid="tab-done"
            size="sm"
          >
            <FolderCheck className="w-4 h-4" />
            Afgehandeld ({doneCount})
          </Button>
        </div>

        {visibleOrders.length === 0 ? (
          <Card>
            <CardContent className="py-16">
              <div className="empty-state">
                <ShoppingCart className="w-20 h-20 mx-auto mb-4 text-zinc-300" />
                <h3 className="font-barlow text-xl font-bold uppercase text-zinc-700 mb-2">
                  {t('order.noOrders')}
                </h3>
                <p className="text-zinc-500">{t('adminOrders.ordersAppearHere')}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {visibleOrders.map((order) => (
                <Card key={order.id} data-testid={`order-card-${order.id}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-semibold text-zinc-900">{order.dealer_company}</p>
                        <p className="text-sm text-zinc-500">{order.dealer_email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(order.status)}
                        <DriverRoutePicker order={order} counts={driverCounts.counts} max={driverCounts.max} onChange={onDriverChange} testid={`toggle-route-${order.id}`} />
                      </div>
                    </div>
                    
                    <div className="bg-zinc-50 rounded-lg p-3 mb-3">
                      {order.motorcycle ? (
                        <>
                          <p className="font-medium text-zinc-900">
                            {order.motorcycle.brand} {order.motorcycle.model}
                          </p>
                          <p className="text-sm text-zinc-500">
                            {order.motorcycle.year} • {order.motorcycle.color}
                          </p>
                          <p className="font-barlow font-bold text-red-600 text-lg mt-1">
                            {formatPrice(order.motorcycle.price)}
                          </p>
                          {order.motorcycle.original_price && (
                            <p className="text-xs text-zinc-500 mt-0.5">
                              Lev: {order.motorcycle.original_currency === 'CHF' 
                                ? `CHF ${order.motorcycle.original_price.toLocaleString('nl-NL')}` 
                                : formatPrice(order.motorcycle.original_price)}
                            </p>
                          )}
                          {/* Order options badges */}
                          <div className="flex flex-wrap gap-1 mt-2" data-testid={`order-options-${order.id}`}>
                            {/mundi/i.test(order.motorcycle?.foreign_dealer_company || order.motorcycle_snapshot?.foreign_dealer_company || '') && (
                              <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-xs" data-testid={`eu-model-${order.id}`}>🇪🇺 EU-model — geen keuring</Badge>
                            )}
                            {order.needs_coc && (
                              <Badge className="bg-purple-100 text-purple-800 text-xs">
                                COC/CVO €{order.coc_cost || 0}
                              </Badge>
                            )}
                            {order.needs_inspection && (
                              <Badge className="bg-blue-100 text-blue-800 text-xs">Keuring €{order.inspection_cost || 125}</Badge>
                            )}
                            {order.needs_valuation && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs">Taxatie €{order.valuation_cost || 160}</Badge>
                            )}
                            {order.needs_delivery && (
                              <Badge className="bg-zinc-100 text-zinc-800 text-xs">Bezorging €{order.delivery_cost || 50}</Badge>
                            )}
                          </div>
                        </>
                      ) : (
                        <span className="text-zinc-400">{t('motorcycle.deleted')}</span>
                      )}
                    </div>
                    
                    {order.notes && (
                      <p className="text-sm text-zinc-600 mb-3 line-clamp-2">{order.notes}</p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-zinc-400">
                        {new Date(order.created_at).toLocaleDateString('nl-NL', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                      <div className="flex gap-2">
                        {order.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => updateStatus(order.id, 'approved')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200"
                              onClick={() => updateStatus(order.id, 'rejected')}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {order.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateStatus(order.id, 'completed')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            {t('adminOrders.complete')}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/pakbon/${order.id}`)}
                          title={t('adminOrders.printPakbon')}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {eprintEnabled && eprintEmail && (
                          <Button
                            size="sm"
                            variant="outline"
                            className={order.eprint_sent_at ? 'text-green-600 border-green-200' : 'text-blue-600 border-blue-200'}
                            onClick={() => printOrderToEprint(order.id)}
                            disabled={printingOrderId === order.id}
                            data-testid={`eprint-mobile-btn-${order.id}`}
                            title={order.eprint_sent_at ? `Pakbon gemaild op ${new Date(order.eprint_sent_at).toLocaleString('nl-NL')}` : 'Print naar HP printer via e-mail'}
                          >
                            {printingOrderId === order.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          </Button>
                        )}
                        {order.needs_delivery && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-200"
                            onClick={() => openTransportDialog(order)}
                            title="Transport status"
                          >
                            <Truck className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Desktop Table View */}
            <Card className="hidden md:block">
              <CardContent className="p-0">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <th>{t('order.date')}</th>
                      <th>{t('nav.dealers')}</th>
                      <th>{t('motorcycle.singular')}</th>
                      <th>{t('motorcycle.price')}</th>
                      <th>{t('adminOrders.notes')}</th>
                      <th>{t('order.status')}</th>
                      <th>{t('adminOrders.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleOrders.map((order) => (
                      <tr key={order.id} data-testid={`order-row-${order.id}`}>
                        <td className="text-zinc-500">
                          {new Date(order.created_at).toLocaleDateString('nl-NL', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </td>
                        <td>
                          <div>
                            <p className="font-semibold text-zinc-900">{order.dealer_company}</p>
                            <p className="text-sm text-zinc-500">{order.dealer_email}</p>
                          </div>
                        </td>
                        <td>
                          {order.motorcycle ? (
                            <div>
                              <p className="font-medium text-zinc-900">
                                {order.motorcycle.brand} {order.motorcycle.model}
                              </p>
                              <p className="text-sm text-zinc-500">
                                {order.motorcycle.year} • {order.motorcycle.color}
                              </p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {order.needs_coc && (
                                  <Badge className="bg-purple-100 text-purple-800 text-xs" data-testid={`coc-badge-${order.id}`}>
                                    COC €{order.coc_cost || 0}
                                  </Badge>
                                )}
                                {order.needs_inspection && (
                                  <Badge className="bg-blue-100 text-blue-800 text-xs">Keur</Badge>
                                )}
                                {order.needs_valuation && (
                                  <Badge className="bg-amber-100 text-amber-800 text-xs">Tax</Badge>
                                )}
                                {order.needs_delivery && (
                                  <Badge className="bg-zinc-100 text-zinc-800 text-xs">Bezorg</Badge>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-zinc-400">{t('motorcycle.deleted')}</span>
                          )}
                        </td>
                        <td>
                          {order.motorcycle ? (
                            <div>
                              <span className="font-barlow font-bold text-red-600">
                                {formatPrice(order.motorcycle.price)}
                              </span>
                              {order.motorcycle.original_price && (
                                <p className="text-xs text-zinc-500 mt-0.5">
                                  Lev: {order.motorcycle.original_currency === 'CHF' 
                                    ? `CHF ${order.motorcycle.original_price.toLocaleString('nl-NL')}` 
                                    : formatPrice(order.motorcycle.original_price)}
                                </p>
                              )}
                            </div>
                          ) : '-'}
                        </td>
                        <td className="max-w-xs">
                          <p className="text-sm text-zinc-600 truncate">
                            {order.notes || '-'}
                          </p>
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {getStatusBadge(order.status)}
                            <DriverRoutePicker order={order} counts={driverCounts.counts} max={driverCounts.max} onChange={onDriverChange} small testid={`toggle-route-desktop-${order.id}`} />
                          </div>
                        </td>
                        <td>
                          <div className="flex gap-2 flex-wrap items-center">
                            {/* Pakbon status badges */}
                            {order.pakbon_completed ? (
                              <Badge className="bg-green-100 text-green-700 text-xs gap-1" data-testid={`pakbon-done-badge-${order.id}`}>
                                <CheckCircle className="w-3 h-3" />
                                Afgehandeld
                              </Badge>
                            ) : order.pakbon_picked_up ? (
                              <Badge className="bg-blue-100 text-blue-700 text-xs gap-1" data-testid={`pakbon-picked-badge-${order.id}`}>
                                <Package className="w-3 h-3" />
                                Opgehaald
                              </Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 text-xs gap-1" data-testid={`pakbon-topr-badge-${order.id}`}>
                                <FolderOpen className="w-3 h-3" />
                                Nog uitprinten
                              </Badge>
                            )}

                            {order.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  onClick={() => updateStatus(order.id, 'approved')}
                                  data-testid={`approve-btn-${order.id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => updateStatus(order.id, 'rejected')}
                                  data-testid={`reject-btn-${order.id}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            {order.status === 'approved' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateStatus(order.id, 'completed')}
                                data-testid={`complete-btn-${order.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {t('adminOrders.complete')}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/pakbon/${order.id}`)}
                              data-testid={`pakbon-btn-${order.id}`}
                              title={t('adminOrders.printPakbon')}
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                            {eprintEnabled && eprintEmail && (
                              <Button
                                size="sm"
                                variant="outline"
                                className={`gap-1 ${order.eprint_sent_at ? 'text-green-600 border-green-200' : 'text-blue-600 border-blue-200'}`}
                                onClick={() => printOrderToEprint(order.id)}
                                disabled={printingOrderId === order.id}
                                data-testid={`eprint-btn-${order.id}`}
                                title={order.eprint_sent_at ? `Pakbon gemaild op ${new Date(order.eprint_sent_at).toLocaleString('nl-NL')}` : 'Print naar HP printer via e-mail'}
                              >
                                {printingOrderId === order.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {order.needs_delivery && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-blue-600 border-blue-200"
                                onClick={() => openTransportDialog(order)}
                                title="Transport status"
                              >
                                <Truck className="w-4 h-4" />
                              </Button>
                            )}

                            {/* Pakbon folder actions */}
                            {!order.pakbon_completed && !order.pakbon_picked_up && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-blue-300 text-blue-700 hover:bg-blue-50 gap-1 text-xs"
                                onClick={() => markPakbonPickedUp(order.id, true)}
                                data-testid={`mark-picked-up-${order.id}`}
                                title="Markeer als opgehaald"
                              >
                                <Package className="w-3.5 h-3.5" />
                                Opgehaald
                              </Button>
                            )}
                            {!order.pakbon_completed && order.pakbon_picked_up && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-green-300 text-green-700 hover:bg-green-50 gap-1 text-xs"
                                  onClick={() => markPakbonCompleted(order.id, true)}
                                  data-testid={`mark-completed-${order.id}`}
                                  title="Pakbon afhandelen"
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Afhandelen
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-zinc-300 text-zinc-600 hover:bg-zinc-50 text-xs"
                                  onClick={() => markPakbonPickedUp(order.id, false)}
                                  data-testid={`unmark-picked-up-${order.id}`}
                                  title="Terug naar 'Nog uitprinten'"
                                >
                                  ↶
                                </Button>
                              </>
                            )}
                            {order.pakbon_completed && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-zinc-300 text-zinc-600 hover:bg-zinc-50 text-xs"
                                onClick={() => markPakbonCompleted(order.id, false)}
                                data-testid={`unmark-completed-${order.id}`}
                                title="Terug naar Opgehaald"
                              >
                                ↶
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Transport Status Dialog */}
      <Dialog open={transportDialogOpen} onOpenChange={setTransportDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-blue-600" />
              Transport Status Bijwerken
            </DialogTitle>
            <DialogDescription>
              {selectedOrder?.motorcycle_brand} {selectedOrder?.motorcycle_model} - {selectedOrder?.dealer_company}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Status Select */}
            <div className="space-y-2">
              <Label>Transport Status</Label>
              <Select
                value={transportData.transport_status}
                onValueChange={(value) => setTransportData({...transportData, transport_status: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Wachtend op transport
                    </span>
                  </SelectItem>
                  <SelectItem value="picked_up">
                    <span className="flex items-center gap-2">
                      <Package className="w-4 h-4" /> Opgehaald door transporteur
                    </span>
                  </SelectItem>
                  <SelectItem value="in_transit">
                    <span className="flex items-center gap-2">
                      <Truck className="w-4 h-4" /> Onderweg
                    </span>
                  </SelectItem>
                  <SelectItem value="delivered">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> Afgeleverd
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Carrier */}
            <div className="space-y-2">
              <Label>Transporteur</Label>
              <Input
                placeholder="Bijv. DHL, PostNL, eigen transport..."
                value={transportData.transport_carrier}
                onChange={(e) => setTransportData({...transportData, transport_carrier: e.target.value})}
              />
            </div>

            {/* Tracking Number */}
            <div className="space-y-2">
              <Label>Trackingnummer (optioneel)</Label>
              <Input
                placeholder="Track & trace code"
                value={transportData.transport_tracking_number}
                onChange={(e) => setTransportData({...transportData, transport_tracking_number: e.target.value})}
              />
            </div>

            {/* Estimated Delivery */}
            <div className="space-y-2">
              <Label>Verwachte leverdatum</Label>
              <Input
                type="date"
                value={transportData.transport_estimated_delivery}
                onChange={(e) => setTransportData({...transportData, transport_estimated_delivery: e.target.value})}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Opmerkingen</Label>
              <Textarea
                placeholder="Extra informatie over het transport..."
                value={transportData.transport_notes}
                onChange={(e) => setTransportData({...transportData, transport_notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTransportDialogOpen(false)}>
              Annuleren
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={saveTransportStatus}
              disabled={savingTransport}
            >
              {savingTransport ? 'Opslaan...' : 'Opslaan & Email Versturen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Layout>
  );
};

export default OrderList;
